'use client'

import { MinimalTemplate } from '@payloadcms/next/templates';
import { Button, Form, Modal, SelectInput, toast, useConfig, useModal, useTranslation, XIcon } from '@payloadcms/ui';
import { BeforeListTableClientProps } from 'payload';
import React, { useState } from 'react';
import { LlmApp, LlmRulesResult } from '../../api/types';
import './GenerateRulesModal.scss';

const baseClass = 'generate-rules-options';

type LlmAppOption = {
  label: string;
  value: string;
};

export const BtnGenerateRulesVectorDb: React.FC<BeforeListTableClientProps> = (props) => {
  const { config } = useConfig();
  const { routes: { api: apiRoute } = {} } = config;
  const modalKey = 'generate-rules-modal';
  const { openModal, isModalOpen, closeAllModals } = useModal();
  const [loading, setLoading] = React.useState(false);
  const [llmApps, setllmApps] = useState<LlmAppOption[]>([]);
  const [selectedLlmApp, setSelectedLlmApp] = React.useState('');
  const { t } = useTranslation<any, any>();

  const generateRules = async (appId: string) => {
    setLoading(true);
    try {
      const httpResponse = await fetch(`${apiRoute}/llm/rules`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        referrer: '',
        body: JSON.stringify({
          appId: appId,
        }),
      });
      if (httpResponse.ok) {
        const response = (await httpResponse.json()) as LlmRulesResult[];
        const gotError = response.some((element) => element.status === 'error');
        if (!gotError) {
          toast.success(t('general:generateRulesSuccess'));
        } else {
          const toastMsg = (
            <div>
              <div>{t('general:generateRulesError')}</div>
              <ul>
                {response
                  .filter((element) => element.status === 'error')
                  .map((element) => (
                    <li>
                      <div>
                        {`App: ${element.name}`} - {element.error}
                      </div>
                    </li>
                  ))}
              </ul>
            </div>
          );
          toast.error(toastMsg, {}); // { autoClose: false });
        }
      } else {
        throw new Error(`Response status: ${httpResponse.status}`);
      }
    } catch (error: unknown) {
      console.error(`${t('general:generateRulesError')}: ${error}`);
      toast.error(`${t('general:generateRulesError')}: ${error}`, {}); // { autoClose: false });
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async () => {
    try {
      // generate rules for the selected app
      generateRules(selectedLlmApp);
      closeAllModals();
    } catch (error) {
      console.error(t('general:generateRulesError') + ': ', error);
      toast.error(t('general:generateRulesError'));
    }
  };

  const handleClick = async () => {
    try {
      // get user apps for which it's possible to generate rules
      const httpResponse = await fetch(`${apiRoute}/llm/apps`, {
        method: 'GET',
      });
      const response = await httpResponse.json();
      if (httpResponse.ok) {
        const apps: LlmApp[] = response;
        setllmApps(apps.map((app) => ({ value: app.id, label: app.name })));
        if (apps.length > 1) {
          // user has many apps: open modal to select the app for which to generate rules
          openModal(modalKey);
        } else {
          // user has only one app: directly generate rules for that app
          await generateRules(apps[0]!.id);
        }
      } else {
        toast.error(response.message);
        return;
      }
    } catch (error: unknown) {
      console.error(t('general:generateRulesError') + ': ', error);
      toast.error(t('general:generateRulesError'));
    }
  };

  return (
    <div>
      <Button onClick={handleClick} disabled={loading}>
        {loading ? t('general:generatingRules') : t('general:generateRules')}
      </Button>
      <Modal slug={modalKey} className={`${baseClass}__modal`}>
        {isModalOpen(modalKey) && (
          <MinimalTemplate>
            <header className={`${baseClass}__header`}>
              <h3>{t('general:generateRulesModalTitle')}</h3>
              <Button buttonStyle="none" onClick={closeAllModals}>
                <XIcon />
              </Button>
            </header>
            <main className={`${baseClass}__main`}>
              <Form onSubmit={onSubmit}>
                <div className="render-fields">
                  <SelectInput
                    label={t('general:selectLlmApp')}
                    name="llmApp"
                    path={'llmApp'}
                    options={llmApps}
                    value={selectedLlmApp}
                    onChange={(e: any) => setSelectedLlmApp(e.value)}
                  />
                </div>
                <div className={`${baseClass}__foot`}>
                  <Button type="submit">{t('general:submit')}</Button>
                </div>
              </Form>
            </main>
          </MinimalTemplate>
        )}
      </Modal>
    </div>
  );
};
